// 全产品**唯一一套图标** —— 线性 SVG,不是 emoji。
//
// ★ 为什么不能用 emoji(💬 📁 ⚙ 🗑 ✏️ ▶)
//   emoji 是**彩色位图**,由系统字体渲染:每个的实际笔画粗细、视觉重量、基线、
//   内边距都不一样(💬 偏胖、📁 是实心彩色、⚙ 是细线、▶ 是几何字符没有内边距)。
//   所以「这排图标看起来不一样」不是没调好 —— 是 emoji 本身就**不可能一致**:
//   它们的字形来自不同年代、不同设计师、不同栅格,而且**跟随系统主题变色**,
//   在暗色底上是另一副样子。currentColor 对它们无效。
//   换成同一套 SVG:同一个 24 网格、同一个 stroke 宽度(1.6)、同一个圆角、
//   同一个 currentColor。视觉重量**由构造保证**,不靠肉眼调。
//
// ★ 规格(改图标时照抄,别自创)
//   viewBox 0 0 24 24 · fill none · stroke currentColor · stroke-width 1.6
//   linecap/linejoin round · 主体大致落在 3..21 的安全区里(留 3px 光学内边距)
//
// ★ 为什么不引 lucide / phosphor:多一个运行时依赖 + 一套别人的规格,
//   而我们只需要 30 个图标。自己画,规格才是我们说了算。
//
// ★ 这个文件**不 import react**(连 type 都不 import)—— 和 RailIcon 一样。
//   packages/* 在 frontend 的 `tsc -b` 工程里解析不到 react 的类型
//   (ErrorBoundary.tsx 至今为此报错)。不 import,就绕开这条已知的工程配置坑。

const P = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

// 少数图标需要实心点/实心块(●、■)—— 它们是**状态点**不是线条图形。
const F = { fill: 'currentColor', stroke: 'none' };

export type IconName =
  // rail / 导航
  | 'help' | 'settings' | 'chat' | 'files'
  // 文件与素材
  | 'folder' | 'folder-open' | 'file' | 'image' | 'video' | 'audio' | 'mic' | 'code' | 'paperclip' | 'note'
  // 动作
  | 'download' | 'upload' | 'refresh' | 'undo' | 'redo' | 'trash' | 'archive' | 'unarchive' | 'copy' | 'edit' | 'eye'
  | 'scissors' | 'save' | 'search' | 'send' | 'link' | 'plus' | 'close' | 'external-link'
  // 播放
  | 'play' | 'pause' | 'stop' | 'record' | 'volume' | 'volume-off' | 'clapperboard'
  // 状态
  | 'sparkle' | 'warning' | 'check' | 'cross' | 'clock' | 'hourglass' | 'dot' | 'circle' | 'circle-slash' | 'bolt'
  | 'brain' | 'user' | 'lock' | 'key' | 'bug' | 'gem' | 'tools' | 'cloud' | 'monitor' | 'star' | 'droplet'
  // 方向
  | 'chevron-right' | 'chevron-down' | 'chevron-up' | 'chevron-left' | 'arrow-right' | 'arrow-up'
  // 视图
  | 'grid' | 'list';

const PATHS = {
  // ── rail ──────────────────────────────────────────────────────────
  chat: <path {...P} d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.8 9.8 0 0 1-3.3-.6L3 21l1.8-4.3A8.2 8.2 0 0 1 3.6 11.5a8.4 8.4 0 0 1 9-8.4 8.4 8.4 0 0 1 8.4 8.4Z" />,
  files: <path {...P} d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.5h9a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-10Z" />,
  help: (
    <>
      <circle {...P} cx="12" cy="12" r="9" />
      <path {...P} d="M9.2 9.2a2.9 2.9 0 0 1 5.6 1c0 1.9-2.8 2.4-2.8 2.4" />
      <path {...P} d="M12 17h.01" />
    </>
  ),
  settings: (
    <>
      <circle {...P} cx="12" cy="12" r="3" />
      <path {...P} d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
    </>
  ),

  // ── 文件与素材 ────────────────────────────────────────────────────
  folder: <path {...P} d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.5h9a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-10Z" />,
  'folder-open': (
    <>
      <path {...P} d="M3 17.5v-10A1.5 1.5 0 0 1 4.5 6h4l2 2.5h9a1.5 1.5 0 0 1 1.5 1.5v1" />
      <path {...P} d="M3.2 18.6 5.6 12.4a1.5 1.5 0 0 1 1.4-1h14a1 1 0 0 1 .94 1.36l-2.1 5.5a1.5 1.5 0 0 1-1.4.96H4.5A1.4 1.4 0 0 1 3.2 18.6Z" />
    </>
  ),
  file: (
    <>
      <path {...P} d="M14 3H7a1.5 1.5 0 0 0-1.5 1.5v15A1.5 1.5 0 0 0 7 21h10a1.5 1.5 0 0 0 1.5-1.5V7.5L14 3Z" />
      <path {...P} d="M14 3v3.5a1 1 0 0 0 1 1h3.5" />
    </>
  ),
  image: (
    <>
      <rect {...P} x="3.5" y="4.5" width="17" height="15" rx="2" />
      <circle {...P} cx="9" cy="9.8" r="1.6" />
      <path {...P} d="m4 16.5 4.2-4a1.6 1.6 0 0 1 2.2 0l3.4 3.3a1.6 1.6 0 0 0 2.2 0L18 14l2.5 2.4" />
    </>
  ),
  video: (
    <>
      <rect {...P} x="3" y="6" width="12.5" height="12" rx="2" />
      <path {...P} d="m15.5 13.2 4 2.6a.7.7 0 0 0 1.1-.6V8.8a.7.7 0 0 0-1.1-.6l-4 2.6Z" />
    </>
  ),
  audio: (
    <>
      <path {...P} d="M11.5 5.5 7 9H4.2a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1H7l4.5 3.5a.6.6 0 0 0 1-.5V6a.6.6 0 0 0-1-.5Z" />
      <path {...P} d="M16 9.4a3.6 3.6 0 0 1 0 5.2M18.7 7a7 7 0 0 1 0 10" />
    </>
  ),
  mic: (
    <>
      <rect {...P} x="9.4" y="3" width="5.2" height="11" rx="2.6" />
      <path {...P} d="M6 11.5a6 6 0 0 0 12 0M12 17.5V21M9 21h6" />
    </>
  ),
  code: <path {...P} d="m8.5 8-4.5 4 4.5 4M15.5 8l4.5 4-4.5 4M13.5 5l-3 14" />,
  paperclip: <path {...P} d="M20 11.5 12.3 19a4.6 4.6 0 0 1-6.5-6.5l7.7-7.7a3 3 0 0 1 4.3 4.3l-7.7 7.7a1.5 1.5 0 0 1-2.2-2.2l7.1-7.1" />,
  note: (
    <>
      <path {...P} d="M20 12.5V5.5A1.5 1.5 0 0 0 18.5 4h-13A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20h7" />
      <path {...P} d="M8 8.5h8M8 12h5M8 15.5h3" />
      <path {...P} d="m15 20 5-5" />
    </>
  ),

  // ── 动作 ──────────────────────────────────────────────────────────
  download: <path {...P} d="M12 3.5v11m0 0 4-4m-4 4-4-4M4 16.5v2A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-2" />,
  upload: <path {...P} d="M12 20.5v-11m0 0 4 4m-4-4-4 4M4 7.5v-2A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v2" />,
  refresh: <path {...P} d="M20 12a8 8 0 1 1-2.5-5.8M20 4v4.5h-4.5" />,
  undo: <path {...P} d="M8.5 5 4 9.5 8.5 14M4 9.5h10a5.5 5.5 0 0 1 0 11h-3" />,
  redo: <path {...P} d="M15.5 5 20 9.5 15.5 14M20 9.5H10a5.5 5.5 0 0 0 0 11h3" />,
  trash: (
    <>
      <path {...P} d="M4.5 6.5h15M9.5 6.5V5A1.5 1.5 0 0 1 11 3.5h2A1.5 1.5 0 0 1 14.5 5v1.5" />
      <path {...P} d="M6.5 6.5 7.3 19a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4l.8-12.5" />
      <path {...P} d="M10.3 10v6.5M13.7 10v6.5" />
    </>
  ),
  archive: (
    <>
      <rect {...P} x="3.5" y="4" width="17" height="4" rx="1" />
      <path {...P} d="M5.2 8v10.5A1.5 1.5 0 0 0 6.7 20h10.6a1.5 1.5 0 0 0 1.5-1.5V8" />
      <path {...P} d="M10 12h4" />
    </>
  ),
  unarchive: (
    <>
      <rect {...P} x="3.5" y="4" width="17" height="4" rx="1" />
      <path {...P} d="M5.2 8v10.5A1.5 1.5 0 0 0 6.7 20h10.6a1.5 1.5 0 0 0 1.5-1.5V8" />
      <path {...P} d="M12 17v-6m0 0-2.4 2.4M12 11l2.4 2.4" />
    </>
  ),
  copy: (
    <>
      <rect {...P} x="9" y="9" width="11" height="11" rx="2" />
      <path {...P} d="M15 6.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h.5" />
    </>
  ),
  edit: <path {...P} d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3ZM14.5 6.5l3 3" />,
  eye: (
    <>
      <path {...P} d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12Z" />
      <circle {...P} cx="12" cy="12" r="3" />
    </>
  ),
  scissors: (
    <>
      <circle {...P} cx="6.5" cy="18" r="2.5" />
      <circle {...P} cx="6.5" cy="6" r="2.5" />
      <path {...P} d="m8.6 7.4 11.4 9.2M20 7.4 8.6 16.6" />
    </>
  ),
  save: (
    <>
      <path {...P} d="M5.5 4h10.2L20 8.3v11.2A1.5 1.5 0 0 1 18.5 21h-13A1.5 1.5 0 0 1 4 19.5v-14A1.5 1.5 0 0 1 5.5 4Z" />
      <path {...P} d="M8 4v5h7V4M8 21v-6h8v6" />
    </>
  ),
  search: (
    <>
      <circle {...P} cx="10.8" cy="10.8" r="6.3" />
      <path {...P} d="m15.5 15.5 4.3 4.3" />
    </>
  ),
  send: <path {...P} d="M12 20V4m0 0 6.5 6.5M12 4 5.5 10.5" />,
  link: (
    <>
      <path {...P} d="M10 13.8a3.6 3.6 0 0 0 5.4.4l2.6-2.6a3.6 3.6 0 1 0-5.1-5.1L11.4 8" />
      <path {...P} d="M14 10.2a3.6 3.6 0 0 0-5.4-.4L6 12.4a3.6 3.6 0 1 0 5.1 5.1L12.6 16" />
    </>
  ),
  plus: <path {...P} d="M12 5v14M5 12h14" />,
  close: <path {...P} d="m6.5 6.5 11 11M17.5 6.5l-11 11" />,
  'external-link': <path {...P} d="M14 4h6v6M20 4l-8.5 8.5M18 14.5v4A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6h4" />,

  // ── 播放 ──────────────────────────────────────────────────────────
  play: <path {...P} d="M7.5 5.4v13.2a.8.8 0 0 0 1.22.68l10.5-6.6a.8.8 0 0 0 0-1.36L8.72 4.72A.8.8 0 0 0 7.5 5.4Z" />,
  pause: <path {...P} d="M9 5v14M15 5v14" />,
  stop: <rect {...P} x="6" y="6" width="12" height="12" rx="1.6" />,
  record: (
    <>
      <circle {...P} cx="12" cy="12" r="8.5" />
      <circle {...F} cx="12" cy="12" r="4.2" />
    </>
  ),
  volume: (
    <>
      <path {...P} d="M11.5 5.5 7 9H4.2a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1H7l4.5 3.5a.6.6 0 0 0 1-.5V6a.6.6 0 0 0-1-.5Z" />
      <path {...P} d="M16 9.4a3.6 3.6 0 0 1 0 5.2" />
    </>
  ),
  'volume-off': (
    <>
      <path {...P} d="M11.5 5.5 7 9H4.2a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1H7l4.5 3.5a.6.6 0 0 0 1-.5V6a.6.6 0 0 0-1-.5Z" />
      <path {...P} d="m16 10 4.5 4.5M20.5 10 16 14.5" />
    </>
  ),
  clapperboard: (
    <>
      <path {...P} d="M3.5 9.5h17v8.5a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5V9.5Z" />
      <path {...P} d="M3.9 9.5 3.2 6.6a1 1 0 0 1 .75-1.2l14.1-3.1a1 1 0 0 1 1.2.76l.6 2.8" />
      <path {...P} d="m8.1 8.9-1.5-4M13 7.8l-1.5-4M17.9 6.7l-1.5-4" />
    </>
  ),

  // ── 状态 ──────────────────────────────────────────────────────────
  sparkle: <path {...P} d="M12 3.2c.5 3.6 1.8 5.7 5.4 6.6-3.6.9-4.9 3-5.4 6.6-.5-3.6-1.8-5.7-5.4-6.6 3.6-.9 4.9-3 5.4-6.6ZM18.4 15.5c.25 1.6.85 2.5 2.6 2.9-1.75.4-2.35 1.3-2.6 2.9-.25-1.6-.85-2.5-2.6-2.9 1.75-.4 2.35-1.3 2.6-2.9Z" />,
  warning: (
    <>
      <path {...P} d="M10.7 4.1 2.9 17.4a1.5 1.5 0 0 0 1.3 2.25h15.6a1.5 1.5 0 0 0 1.3-2.25L13.3 4.1a1.5 1.5 0 0 0-2.6 0Z" />
      <path {...P} d="M12 9.5v4M12 16.8h.01" />
    </>
  ),
  check: <path {...P} d="m4.8 12.6 4.6 4.6L19.2 7.4" />,
  cross: <path {...P} d="m6.5 6.5 11 11M17.5 6.5l-11 11" />,
  clock: (
    <>
      <circle {...P} cx="12" cy="12" r="8.6" />
      <path {...P} d="M12 7.2V12l3.2 2" />
    </>
  ),
  hourglass: (
    <>
      <path {...P} d="M7 3.5h10M7 20.5h10" />
      <path {...P} d="M7.8 3.5v3.1c0 .8.3 1.6.9 2.2L12 12l-3.3 3.2c-.6.6-.9 1.4-.9 2.2v3.1" />
      <path {...P} d="M16.2 3.5v3.1c0 .8-.3 1.6-.9 2.2L12 12l3.3 3.2c.6.6.9 1.4.9 2.2v3.1" />
    </>
  ),
  dot: <circle {...F} cx="12" cy="12" r="4" />,
  circle: <circle {...P} cx="12" cy="12" r="4.2" />,
  // 「从列表移除」—— 不是删除(trash),是**不再列出**。所以是「禁止/移出」而不是垃圾桶。
  'circle-slash': (
    <>
      <circle {...P} cx="12" cy="12" r="8.4" />
      <path {...P} d="m6.1 6.1 11.8 11.8" />
    </>
  ),
  bolt: <path {...P} d="M13.4 2.5 4.9 13.1a.7.7 0 0 0 .55 1.15H11l-.4 7.25 8.5-10.6a.7.7 0 0 0-.55-1.15H13l.4-7.25Z" />,
  brain: (
    <>
      <path {...P} d="M12 5.2a2.7 2.7 0 0 0-5 1.35A2.8 2.8 0 0 0 4.6 9.3a2.8 2.8 0 0 0 .5 1.6 2.9 2.9 0 0 0 .5 3.9A2.9 2.9 0 0 0 8.8 19a2.6 2.6 0 0 0 3.2.6V5.2Z" />
      <path {...P} d="M12 5.2a2.7 2.7 0 0 1 5 1.35 2.8 2.8 0 0 1 2.4 2.75c0 .58-.18 1.13-.5 1.6a2.9 2.9 0 0 1-.5 3.9A2.9 2.9 0 0 1 15.2 19a2.6 2.6 0 0 1-3.2.6V5.2Z" />
    </>
  ),
  user: (
    <>
      <circle {...P} cx="12" cy="8.2" r="3.7" />
      <path {...P} d="M4.8 20a7.2 7.2 0 0 1 14.4 0" />
    </>
  ),
  lock: (
    <>
      <rect {...P} x="4.8" y="10.2" width="14.4" height="10" rx="2" />
      <path {...P} d="M8.2 10.2V7.6a3.8 3.8 0 0 1 7.6 0v2.6" />
    </>
  ),
  key: (
    <>
      <circle {...P} cx="7.8" cy="16.2" r="3.6" />
      <path {...P} d="m10.4 13.6 8-8M16.2 7.8l2 2M18.6 5.4l2 2" />
    </>
  ),
  bug: (
    <>
      <rect {...P} x="8" y="7" width="8" height="12" rx="4" />
      <path {...P} d="M8 12H4M20 12h-4M8.6 8 6.5 5.9M15.4 8l2.1-2.1M8.6 17.8 6.4 20M15.4 17.8 17.6 20" />
    </>
  ),
  gem: <path {...P} d="M7 4h10l4 5-9 11L3 9l4-5Z M3 9h18M9.5 9 12 20M14.5 9 12 20M7 4l2.5 5M17 4l-2.5 5" />,
  // 扳手。★ 第一版画出来是一支**钢笔** —— path 编译得过、类型也对,
  //   只有把 67 个图标全渲染出来看一眼才发现。**构建绿 ≠ 图标画对了。**
  tools: (
    <path {...P} d="M14.6 6.2a1 1 0 0 0 0 1.4l1.8 1.8a1 1 0 0 0 1.4 0l3.4-3.4a5.7 5.7 0 0 1-7.4 7.4l-6.6 6.6a2.05 2.05 0 0 1-2.9-2.9l6.6-6.6a5.7 5.7 0 0 1 7.4-7.4l-3.7 3.7Z" />
  ),
  cloud: <path {...P} d="M7 18.5a4.2 4.2 0 0 1-.5-8.37A5.6 5.6 0 0 1 17.4 10a3.9 3.9 0 0 1 .6 8.5H7Z" />,
  monitor: (
    <>
      <rect {...P} x="3" y="4.5" width="18" height="12" rx="1.8" />
      <path {...P} d="M9 20h6M12 16.5V20" />
    </>
  ),
  star: <path {...P} d="m12 3.5 2.7 5.5 6 .9-4.35 4.24 1.03 6-5.38-2.83L6.62 20.1l1.03-6L3.3 9.9l6-.9L12 3.5Z" />,
  droplet: <path {...P} d="M12 3.2s6 6 6 10a6 6 0 0 1-12 0c0-4 6-10 6-10Z" />,

  // ── 方向 ──────────────────────────────────────────────────────────
  'chevron-right': <path {...P} d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
  'chevron-down': <path {...P} d="M5.5 9.5 12 16l6.5-6.5" />,
  'chevron-up': <path {...P} d="M5.5 14.5 12 8l6.5 6.5" />,
  'chevron-left': <path {...P} d="M14.5 5.5 8 12l6.5 6.5" />,
  'arrow-right': <path {...P} d="M4 12h16m0 0-5.5-5.5M20 12l-5.5 5.5" />,
  'arrow-up': <path {...P} d="M12 20V4m0 0-6 6m6-6 6 6" />,

  // ── 视图 ──────────────────────────────────────────────────────────
  grid: (
    <>
      <rect {...P} x="4" y="4" width="7" height="7" rx="1.4" />
      <rect {...P} x="13" y="4" width="7" height="7" rx="1.4" />
      <rect {...P} x="4" y="13" width="7" height="7" rx="1.4" />
      <rect {...P} x="13" y="13" width="7" height="7" rx="1.4" />
    </>
  ),
  list: <path {...P} d="M4 6.5h16M4 12h16M4 17.5h16" />,
};

// 少了哪个图标,这一行就红 —— 完备性由类型保证,不靠"我记得都画了"。
const _exhaustive: Record<IconName, unknown> = PATHS;
void _exhaustive;

export interface IconProps {
  name: IconName;
  /** 边长(px)。默认 16 —— 和正文行高咬合;rail 用 18,空态大图标用 28+。 */
  size?: number;
  className?: string;
  style?: Record<string, string | number>;
  /** 只有当图标是**唯一**的可读内容时才给(否则旁边已经有文字了,留 aria-hidden)。 */
  label?: string;
}

export default function Icon({ name, size = 16, className, style, label }: IconProps) {
  return (
    <svg
      // `.al-ico`(packages/ui/base.css)= 行内时和文字基线咬合 + 在 flex 行里**不被压扁**。
      // 少了它,图标要么浮在大写字母顶上,要么被 flex 拉成椭圆 —— emoji 时代没这个问题
      // (emoji 是字形,自带基线),换成 SVG 就必须自己把这件事补上。
      className={className ? `al-ico ${className}` : 'al-ico'}
      style={style}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {label ? <title>{label}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
